import { is } from "date-fns/locale"
import { ButtonId, Milestone, OrderStage } from "../types/button_stage"

export const STAGE_FLOW: OrderStage[] = [
    "Order Created",
    "Order Accepted",
    "Order Rejected",
    "Documents",
    "Payments",
    "Delivery",
    "Order Closed",
]

type ButtonRules = {
    [key in ButtonId]: {
        disabled: (milestones: Milestone[]) => boolean
        visible: (milestones: Milestone[]) => boolean
    }
}

// Helper functions to work with the new milestone schema
export const getCurrentStage = (milestones: Milestone[]): OrderStage | null => {
    const activeMilestone = milestones?.find((m) => m.status === "Active")
    return activeMilestone ? activeMilestone.stage : null
}

export const getLiveStage= (milestones: Milestone[]): { stage: string, sub_stage:string} | null => {
    const liveMilestone = milestones?.find((m) => m.status === "Active")
    const isCancelled = milestones?.find((m) => m.status === 'Cancelled')
    if(isCancelled) {
        return {
            stage: isCancelled?.stage as string,
            sub_stage: isCancelled?.stage as string,
        }
    }
    const liveStage = liveMilestone?.sub_stages
    if(Array.isArray(liveStage) && liveStage.length > 0){
        return {
            stage: liveMilestone?.stage as string,
            sub_stage: liveStage?.at(-1)?.sub_stage_name as string
        }
    }
    else if(liveMilestone?.stage){
        return {
            stage: '',
            sub_stage: liveMilestone?.stage as string,
        }
    }
    const orderCancelledOrClosed = isOrderCancelledOrClosed(milestones)
    // sort milestone by priority
    const sortedMilestones = [...(milestones || [])]
        .sort((a, b) => a.priority - b.priority)
    if(orderCancelledOrClosed){
        return {
            stage: sortedMilestones?.at(-1)?.stage as string,
            sub_stage: sortedMilestones?.at(-1)?.sub_stages?.at(-1)?.sub_stage_name as string
        }
    }
    const miles = sortedMilestones?.filter((m) => m.status === "Fulfilled")?.at(-1)
    const stage = miles?.stage
    const last_sub_stage = miles?.sub_stages
    if(Array.isArray(last_sub_stage) && last_sub_stage.length > 0){
        return {
            stage: stage as string,
            sub_stage: last_sub_stage?.at(-1)?.sub_stage_name as string
        }
    }
    return {
        stage: stage as string,
        sub_stage: stage as string
    }
}

const getCompletedStages = (milestones: Milestone[]): OrderStage[] => {
    return milestones?.filter((m) => m.status === "Fulfilled").map((m) => m.stage)
}

const isOrderCancelledOrClosed = (milestones: Milestone[]): boolean => {
    const currentStage = getCurrentStage(milestones)
    return currentStage === "Order Closed" || currentStage === "Order Rejected"
}

export const BUTTON_RULES: ButtonRules = {
    confirmOrder: {
        disabled: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            const completedStages = getCompletedStages(milestones)
            if(isOrderCancelledOrClosed(milestones)) return true
            if(milestones?.find((m) => m.status === 'Cancelled')?.stage === 'Order Rejected') return true
            return (currentStage !== "Order Created" && currentStage !== "Order Accepted") && (completedStages?.includes("Order Accepted") || completedStages?.includes('Order Rejected'))
        },
        visible: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            const completedStages = getCompletedStages(milestones)
            return completedStages?.includes("Order Created") || currentStage === "Order Accepted"
        },
    },
    submitPayment: {
        disabled: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            const completedStages = getCompletedStages(milestones)
            if(isOrderCancelledOrClosed(milestones)) return true
            return currentStage !== 'Payments' || completedStages?.includes("Payments")
        },
        visible: function (milestones) {
            return this?.disabled(milestones)
        },
    },
    confirmDelivery: {
        disabled: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            const completedStages = getCompletedStages(milestones)
            if(isOrderCancelledOrClosed(milestones)) return true
            return currentStage !== 'Delivery' || !completedStages?.includes('Payments') || !completedStages?.includes("Documents")
            // return completedStages?.includes('Delivery')
        },
        visible: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            const completedStages = getCompletedStages(milestones)
            return completedStages?.includes("Payments") || currentStage === "Delivery"
        },
    },
    uploadDocuments: {
        disabled: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            const completedStages = getCompletedStages(milestones)
            if(isOrderCancelledOrClosed(milestones)) return true
            return currentStage !== 'Documents' || completedStages?.includes("Documents")
        },
        visible: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            return currentStage !== "Order Closed"
        },
    },
    cancelOrder: {
        disabled: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            return ["Order Closed", "Order Rejected"]?.includes(currentStage || "")
        },
        visible: (milestones) => {
            const currentStage = getCurrentStage(milestones)
            return !["Order Closed", "Delivery"]?.includes(currentStage || "")
        },
    },
    disableAll:{
        disabled: (milestones) => {
            const completedStages = getCompletedStages(milestones)
            return completedStages.includes('Order Closed') || milestones?.find((m) => m.status === 'Cancelled')?.stage === 'Order Rejected'
        },
        visible: () => true,
    }
}

// Example usage function
export function getButtonState(buttonId: ButtonId, milestones: Milestone[]) {
    const rule = BUTTON_RULES[buttonId]
    return {
        disabled: rule.disabled(milestones),
        visible: rule.visible(milestones),
    }
}
